import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { PASSWORD_MIN_LENGTH, checkPasswordPolicy } from '@bekebi/domain';
import { Button } from '../../components/button';
import { useHasPermission } from '../../session/current-user';
import { useCreateMailbox, useMailboxes } from './api';
import { MailServerNotReady, mailboxErrorMessage } from './mailbox-shared';
import { formatQuota, gbTextToBytes, quotaHint, quotaProblem } from './quota';

/**
 * Add mailbox (/app/mailboxes/new, `mail.mailboxes.manage`) — POST /api/mail/mailboxes with exactly
 * the fields the API accepts: address, size and an initial password. The form is offered only
 * while the Mail Server answers, and success is reported only after the API confirms that the
 * Mail Server created the mailbox. The size rules shown here guide the form; the API enforces them.
 * The password stays in this form only: it is not kept after the request, shown again, stored or
 * put in a URL.
 */
export function MailboxCreatePage() {
  const { state } = useMailboxes();
  return (
    <div className="max-w-xl">
      <p className="text-sm">
        <Link
          to="/app/mailboxes"
          className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Mailboxes
        </Link>
      </p>
      <h1 className="mt-2">Add mailbox</h1>
      <p className="mt-1 text-sm text-muted">
        Create a Bekebi email address on the Mail Server. Give the initial password to the person it
        is for; it is not kept by the portal.
      </p>
      <div className="mt-6">
        {state.kind === 'connected' ? <CreateForm /> : <MailServerNotReady state={state} />}
      </div>
    </div>
  );
}

function CreateForm() {
  const unlimited = useHasPermission('mail.mailboxes.manage_unlimited');
  const create = useCreateMailbox();
  const navigate = useNavigate();

  const schema = z
    .object({
      address: z
        .string()
        .trim()
        .min(1, 'Enter the email address.')
        .max(254, 'The email address can be at most 254 characters.')
        .email('Enter a valid email address.'),
      size: z.string().superRefine((value, ctx) => {
        const problem = quotaProblem(gbTextToBytes(value), unlimited);
        if (problem) ctx.addIssue({ code: 'custom', message: problem });
      }),
      password: z.string().superRefine((value, ctx) => {
        const problem = checkPasswordPolicy(value);
        if (problem) ctx.addIssue({ code: 'custom', message: problem });
      }),
      confirmPassword: z.string(),
    })
    .refine((v) => v.password === v.confirmPassword, {
      path: ['confirmPassword'],
      message: 'The passwords do not match.',
    });
  type Fields = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<Fields>({
    resolver: zodResolver(schema),
    defaultValues: { address: '', size: '1', password: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit((values) =>
    create.mutate(
      {
        address: values.address.trim().toLowerCase(),
        quotaBytes: gbTextToBytes(values.size)!,
        password: values.password,
      },
      {
        onSuccess: (mailbox) =>
          void navigate('/app/mailboxes', {
            state: {
              notice: `${mailbox.address} was created with ${formatQuota(mailbox.quotaBytes)}.`,
            },
          }),
        onSettled: () => {
          resetField('password');
          resetField('confirmPassword');
        },
      },
    ),
  );

  const field =
    'mt-1 block h-11 w-full rounded-md border border-line-strong bg-surface px-3 text-sm';
  const error = (name: keyof Fields) =>
    errors[name] ? (
      <p id={`${name}-error`} className="mt-1.5 text-sm text-danger">
        {errors[name].message}
      </p>
    ) : null;
  const described = (name: keyof Fields, hint?: boolean) =>
    [hint ? `${name}-hint` : null, errors[name] ? `${name}-error` : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <>
      {create.isError ? (
        <p
          role="alert"
          className="mb-5 rounded-lg border border-danger-line bg-danger-soft px-4 py-3 text-sm text-fg"
        >
          {mailboxErrorMessage(create.error)} The mailbox was not created.
        </p>
      ) : null}
      <form onSubmit={onSubmit} noValidate aria-label="Add mailbox" className="space-y-5">
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-fg">
            Email address
          </label>
          <input
            id="address"
            type="email"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={errors.address ? 'true' : 'false'}
            aria-describedby={described('address')}
            className={field}
            {...register('address')}
          />
          {error('address')}
        </div>
        <div>
          <label htmlFor="size" className="block text-sm font-medium text-fg">
            Size (GB)
          </label>
          <input
            id="size"
            type="number"
            inputMode="decimal"
            min={1}
            step={0.1}
            {...(unlimited ? {} : { max: 5 })}
            aria-invalid={errors.size ? 'true' : 'false'}
            aria-describedby={described('size', true)}
            className={`${field} max-w-[10rem]`}
            {...register('size')}
          />
          <p id="size-hint" className="mt-1.5 text-xs text-muted">
            {quotaHint(unlimited)}
          </p>
          {error('size')}
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-fg">
            Initial password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            aria-invalid={errors.password ? 'true' : 'false'}
            aria-describedby={described('password', true)}
            className={field}
            {...register('password')}
          />
          <p id="password-hint" className="mt-1.5 text-xs text-muted">
            At least {PASSWORD_MIN_LENGTH} characters.
          </p>
          {error('password')}
        </div>
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-fg">
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            aria-invalid={errors.confirmPassword ? 'true' : 'false'}
            aria-describedby={described('confirmPassword')}
            className={field}
            {...register('confirmPassword')}
          />
          {error('confirmPassword')}
        </div>
        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating mailbox…' : 'Create mailbox'}
          </Button>
          <Link to="/app/mailboxes" className="inline-flex min-h-10 items-center px-2 text-sm">
            Cancel
          </Link>
        </div>
      </form>
    </>
  );
}
